'use client';

import { useLandingTranslations } from './LandingLocaleProvider';
import { Reveal } from './Reveal';

const swimlaneSteps = ['intent', 'plan', 'execution', 'review', 'learn'] as const;

const stepIcons: Record<string, React.ReactNode> = {
  '01': (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
  '02': (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  ),
  '03': (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  '04': (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  ),
  '05': (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  ),
};

function ActorBadge({ actor }: { actor: string }) {
  const isHuman = actor.includes('Human');
  const isAI = actor.includes('AI');
  const isBoth = isHuman && isAI;

  if (isBoth) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-warning">
        Human + AI
      </span>
    );
  }
  if (isHuman) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-success">
        Human
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
      AI
    </span>
  );
}

export function SwimlaneDiagram() {
  const t = useLandingTranslations();

  return (
    <Reveal>
      <section className="mt-16">
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-bold md:text-3xl">{t('swimlaneTitle')}</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-base-content/60">{t('swimlaneSubtitle')}</p>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {swimlaneSteps.map((key, i) => {
            const step = t(`swimlaneSteps.${key}.step`);
            const actor = t(`swimlaneSteps.${key}.actor`);
            const isHuman = actor.includes('Human');
            const isAI = actor.includes('AI');
            const isBoth = isHuman && isAI;

            let borderColor = 'border-primary/20';
            let accentColor = 'text-primary';
            let bgAccent = 'bg-primary/10';
            if (isBoth) {
              borderColor = 'border-warning/20';
              accentColor = 'text-warning';
              bgAccent = 'bg-warning/10';
            } else if (isHuman && !isAI) {
              borderColor = 'border-success/20';
              accentColor = 'text-success';
              bgAccent = 'bg-success/10';
            }

            return (
              <Reveal key={key} delay={i * 80}>
                <div
                  className={`relative overflow-hidden rounded-2xl border ${borderColor} bg-base-100 p-5 shadow-xs h-full`}
                >
                  <div
                    aria-hidden="true"
                    className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent ${isBoth ? 'via-warning/30' : isHuman ? 'via-success/30' : 'via-primary/30'} to-transparent`}
                  />
                  <div className="mb-3 flex items-center gap-3">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${bgAccent} ${accentColor}`}>
                      {stepIcons[step]}
                    </div>
                    <span className={`text-xs font-bold uppercase tracking-widest ${accentColor}`}>{step}</span>
                  </div>
                  <ActorBadge actor={actor} />
                  <h3 className="mt-2 text-base font-semibold">{t(`swimlaneSteps.${key}.title`)}</h3>
                  <p className="mt-1.5 text-sm text-base-content/65">{t(`swimlaneSteps.${key}.description`)}</p>
                  {/* Arrow connector (desktop) */}
                  {i < swimlaneSteps.length - 1 && (
                    <div className="absolute -right-2 top-1/2 z-10 hidden -translate-y-1/2 text-base-content/20 lg:block">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>
    </Reveal>
  );
}
